import { AppShell } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { type ReactNode, useEffect } from "react";

import { RAIL_WIDTH } from "@mountayaapp/theme";

import type { Tab } from "../../content/rules/registry";
import { DocsPathnameProvider } from "../providers/DocsPathnameContext";
import { DocsHeader } from "./DocsHeader";
import { DocsNavbar } from "./DocsNavbar";

/**
 * HEADER_HEIGHT is the height of the persistent two-row top header, reserved by
 * the AppShell so the scrollable regions begin beneath it. It reads the variable
 * `docsChrome.css` declares rather than restating the number, so the reserved space
 * and the root's anchor scroll padding can never disagree.
 */
const HEADER_HEIGHT = "var(--docs-header-height)";

/**
 * ASIDE_WIDTH is the table-of-contents rail width. It sits just above the navigation rail
 * width so the reading column keeps a comfortable measure on wide screens, with enough
 * room for the wider, more legible contents list.
 */
const ASIDE_WIDTH = 300;

/**
 * DocsAppShellProps carry the page body, the active pathname (for chrome active
 * state), the active tab (whose layout decides whether the sidebar shows), and an
 * optional table-of-contents rail. The aside slot is omitted for pages with no
 * headings (the landing and the 404 view) so no empty rail is reserved. `full`
 * drops the main region's padding so a full-bleed page (an embedded explorer)
 * fills the width edge-to-edge below the header.
 */
interface DocsAppShellProps {
  aside?: ReactNode;
  children: ReactNode;
  full?: boolean;
  pathname: string;
  tab: Tab;
}

/**
 * DocsAppShell frames every docs page: a persistent top header above a scrollable
 * main region, with the curated sidebar shown only on tabs whose layout calls for
 * it and an optional table-of-contents rail on wide screens. The bare tab (the
 * changelog) and every full-bleed page drop the sidebar and run full-width.
 * Both side regions collapse below their breakpoints so the content takes the full
 * width on narrow viewports. The active pathname is published to the chrome so the
 * header and sidebar can mark the current tab and page.
 *
 * The three top rails carry deliberately different top padding so the sidebar's
 * first item, the breadcrumb, and the table-of-contents heading line up on one
 * horizontal row: the aside takes `xl` here and the main content container `md` (it
 * already sits inside the shell's own `md` main padding), while the navbar's own inset
 * comes from the shared rail class rather than from this file, so that the application's
 * settings rail opens on the same line. Keep them in sync if any of those change.
 */
export const DocsAppShell = ({ aside, children, full, pathname, tab }: DocsAppShellProps) => {
  // A full-bleed page drops the rail as well as the padding. The three generated references
  // are filed in Documentation's own groups so a reader meets each beside the guides that
  // explain it — and each carries its own navigation, so drawing ours beside it would put two
  // rails on one screen and leave the reading column narrower than the schema in it.
  const withSidebar = tab.layout === "sidebar" && full !== true;
  const [navbarOpened, { close: closeNavbar, toggle: toggleNavbar }] = useDisclosure(false);

  // A full page load resets this state, but a client-side navigation would not,
  // so collapse the mobile navbar drawer whenever the active page changes.
  useEffect(() => {
    closeNavbar();
  }, [pathname, closeNavbar]);

  // Escape closes the open rail. The burger opens it and the burger closes it, which is one
  // affordance for a reader who can point at it and none at all for a reader on a keyboard:
  // they would have to tab through every link in the rail to reach the control again. The
  // listener is bound only while the rail is open, so it takes nothing away from a page whose
  // rail is a permanent column.
  useEffect(() => {
    if (!navbarOpened) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        closeNavbar();
      }
    };

    document.addEventListener("keydown", onKeyDown);

    return () => document.removeEventListener("keydown", onKeyDown);
  }, [closeNavbar, navbarOpened]);

  return (
    <DocsPathnameProvider pathname={pathname}>
      <AppShell
        aside={
          aside ? { breakpoint: "md", collapsed: { mobile: true }, width: ASIDE_WIDTH } : undefined
        }
        header={{ height: HEADER_HEIGHT }}
        navbar={
          withSidebar
            ? {
                breakpoint: "sm",
                collapsed: { desktop: false, mobile: !navbarOpened },
                width: RAIL_WIDTH,
              }
            : undefined
        }
        padding={full ? 0 : "md"}
      >
        <DocsHeader navbarOpened={navbarOpened} onNavbarToggle={toggleNavbar} />
        {withSidebar ? <DocsNavbar tab={tab} /> : null}
        <AppShell.Main>{children}</AppShell.Main>
        {aside ? (
          <AppShell.Aside pb="md" pt="xl" px="md" withBorder={false}>
            {aside}
          </AppShell.Aside>
        ) : null}
      </AppShell>
    </DocsPathnameProvider>
  );
};
